import { useState, useCallback } from 'react';
import { Snackbar, Alert, Box } from '@mui/material';
import type { ProviderCommonProps } from '../../types/provider.types';
import type { SnackbarMessage, SnackbarVariant, SnackbarContextType } from './snackbar.types';
import { SnackbarContext } from './snackbar.context';

const generateId = () => Math.random().toString(36).slice(2, 11);

export const SnackbarProvider = ({ children }: ProviderCommonProps) => {
  const [snackbars, setSnackbars] = useState<SnackbarMessage[]>([]);

  const showSnackbar = useCallback((message: string, variant: SnackbarVariant = 'info', autoHideDuration = 3000) => {
    const id = generateId();
    const newSnackbar: SnackbarMessage = {
      id,
      message,
      variant,
      autoHideDuration,
    };

    setSnackbars((prev) => [...prev, newSnackbar]);
  }, []);

  const hideSnackbar = useCallback((id: string) => {
    setSnackbars((prev) => prev.filter((snackbar) => snackbar.id !== id));
  }, []);

  const showSuccess = useCallback(
    (message: string, autoHideDuration?: number) => {
      showSnackbar(message, 'success', autoHideDuration);
    },
    [showSnackbar],
  );

  const showError = useCallback(
    (message: string, autoHideDuration?: number) => {
      showSnackbar(message, 'error', autoHideDuration);
    },
    [showSnackbar],
  );

  const showWarning = useCallback(
    (message: string, autoHideDuration?: number) => {
      showSnackbar(message, 'warning', autoHideDuration);
    },
    [showSnackbar],
  );

  const showInfo = useCallback(
    (message: string, autoHideDuration?: number) => {
      showSnackbar(message, 'info', autoHideDuration);
    },
    [showSnackbar],
  );

  const value: SnackbarContextType = {
    showSnackbar,
    showSuccess,
    showError,
    showWarning,
    showInfo,
    hideSnackbar,
  };

  return (
    <SnackbarContext.Provider value={value}>
      {children}

      <Box
        sx={{
          position: 'fixed',
          top: 70,
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 1400,
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
          maxWidth: 400,
          alignItems: 'center',
        }}
      >
        {snackbars.map((snackbar, index) => (
          <Snackbar
            key={snackbar.id}
            open={true}
            autoHideDuration={3000}
            onClose={() => hideSnackbar(snackbar.id)}
            sx={{
              position: 'static',
              transform: 'none !important',
              marginBottom: index > 0 ? 1 : 0,
              '& .MuiSnackbar-root': {
                position: 'static',
              },
            }}
          >
            <Alert
              onClose={() => hideSnackbar(snackbar.id)}
              severity={snackbar.variant}
              variant="filled"
              sx={{
                width: '100%',
                borderRadius: 2,
                boxShadow: (theme) => theme.shadows[6],
              }}
            >
              {snackbar.message}
            </Alert>
          </Snackbar>
        ))}
      </Box>
    </SnackbarContext.Provider>
  );
};
